import type { FormEvent, ReactNode } from "react"

/**
 * Read the Rails-emitted CSRF token from `<meta name="csrf-token">`.
 *
 * The application layout (`app/views/layouts/application.html.erb`)
 * renders `<%= csrf_meta_tags %>` which produces both
 * `csrf-param` + `csrf-token` meta tags. Rails accepts the form-
 * submitted token under whatever name the `csrf-param` meta declares
 * — by default `authenticity_token`.
 *
 * Returns empty string if the meta tag isn't present (test
 * environments without the tag, or pre-mount SSR). Submitting an
 * empty token will trigger Rails' CSRF check failure, which is the
 * correct posture (better than silently dropping the protection).
 */
function readCsrfToken(): string {
  if (typeof document === "undefined") return ""
  const meta = document.querySelector<HTMLMetaElement>(
    'meta[name="csrf-token"]',
  )
  return meta?.content ?? ""
}

export interface TiktokOAuthStartFormProps {
  /** Server-resolved POST URL — typically `onboardingOauthStartPath()`,
   *  `tiktokReconnectPath()`, or a `retry_url` from the
   *  connect_failure dispatcher. Browser navigates here on submit;
   *  BE's controller responds with `302 → tiktok.com/v2/auth/authorize`
   *  which the browser follows as a top-level cross-origin nav. */
  action: string
  /** Stable test selector — defaults match existing OKS-22 / OKS-23
   *  testid contracts. Each call site supplies its own. */
  "data-testid"?: string
  /** The submit button — typically `<TiktokConnectCta>`. Wrap it inside
   *  the form so the browser POSTs natively. */
  children: ReactNode
  className?: string
  /**
   * **OKS-311** — Optional submit-time hook fired BEFORE the native
   * browser POST. Used by the onboarding page to flip a local
   * `submitting` state for the CTA loading spinner. We do NOT call
   * `preventDefault` — the native cross-origin nav still happens
   * immediately after this callback. If the BE rescue path 303s back
   * to `/onboarding/tiktok` (failure), the page re-mounts and the
   * loading state resets cleanly.
   */
  onSubmit?: (event: FormEvent<HTMLFormElement>) => void
}

/**
 * **TiktokOAuthStartForm — plain HTML `<form>` for OAuth handoff.**
 *
 * **DO NOT replace with Inertia `<Form>`.** TikTok OAuth (and any
 * cross-origin OAuth provider) requires top-level browser navigation:
 *
 *   1. Browser POSTs to BE.
 *   2. BE responds `302 Location: https://www.tiktok.com/v2/auth/authorize/...`.
 *   3. Browser follows the redirect natively — top-level frame nav.
 *   4. TikTok renders its consent screen.
 *
 * Inertia's `<Form>` submits via XHR (`fetch`). When BE returns a 302
 * to a cross-origin URL, the browser refuses the redirect because
 * cross-origin XHR is gated by CORS preflight. TikTok does NOT send
 * CORS headers (correctly — OAuth providers serve consent UIs only to
 * top-level frames). The XHR fails; the OAuth flow never starts.
 *
 * **Bug class:** OKS-22 + OKS-23 originally used Inertia `<Form>` for
 * every TikTok submit. CTPO caught it during E2E manual testing
 * (2026-05-02). This component is the fix; the test below is the
 * regression canary.
 *
 * **Project memory note** — added per the post-merge sweep: any FE
 * surface that POSTs to a BE endpoint that 302s to an external origin
 * (OAuth providers, payment gateways, anywhere requiring a
 * `<form action="...">` browser-native flow) MUST use this wrapper or
 * an equivalent plain `<form>`. NEVER Inertia `<Form>`.
 *
 * Includes the CSRF token via a hidden `authenticity_token` input
 * (Rails default `csrf-param` name). Read from
 * `<meta name="csrf-token">` at render time — no runtime dependency
 * on the Inertia router.
 */
export function TiktokOAuthStartForm({
  action,
  "data-testid": testId,
  children,
  className = "",
  onSubmit,
}: TiktokOAuthStartFormProps) {
  const csrfToken = readCsrfToken()
  return (
    <form
      method="post"
      action={action}
      className={className}
      data-testid={testId}
      onSubmit={onSubmit}
    >
      {/*
        Hidden CSRF input. Rails accepts the token under
        `authenticity_token` per the layout's `csrf_meta_tags` default.
        If the page rotated the token out from under us (rare), the
        worst case is the BE's CSRF check fails and we render a clean
        422 — preferable to silently dropping the protection.
      */}
      <input type="hidden" name="authenticity_token" value={csrfToken} />
      {children}
    </form>
  )
}

export default TiktokOAuthStartForm
